import axios from 'axios';
import toast from 'react-hot-toast';

const api = axios.create({
baseURL: process.env.REACT_APP_API_URL || 'https://dev-api.cognivault.us/api',
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('cv_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Dedupe AI-unavailable toasts — one visible at a time instead of one per request
let aiToastId = null;

api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('cv_token');
      localStorage.removeItem('cv_user');
      window.location.href = '/login';
    }
    if (err.response?.status === 503 && err.response?.data?.error === 'AI_UNAVAILABLE') {
      const message = err.response.data.message || 'AI service is temporarily unavailable. Please try again in a moment.';
      if (aiToastId) toast.dismiss(aiToastId);
      aiToastId = toast(message, {
        icon: '⚠️',
        duration: 5000,
        style: { background: '#FEF3C7', color: '#78350F', border: '1px solid #FCD34D' }
      });
      err.isAIUnavailable = true;
      err.friendlyMessage = message;
    }
    return Promise.reject(err);
  }
);

export const auth = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

export const records = {
  list: (params) => api.get('/records', { params }),
  get: (id) => api.get(`/records/${id}`),
  upload: (formData) => api.post('/records/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.patch(`/records/${id}`, data),
  publish: (id) => api.post(`/records/${id}/publish`),
  unpublish: (id) => api.post(`/records/${id}/unpublish`),
  delete: (id) => api.delete(`/records/${id}`),
};

export const search = {
  query: (params) => api.get('/search', { params }),
  facets: () => api.get('/search/facets'),
};

export const publicApi = {
  search: (params) => api.get('/public/search', { params }),
  getRecord: (id) => api.get(`/public/records/${id}`),
  facets: () => api.get('/public/facets'),
};

export const migration = {
  jobs: () => api.get('/migration/jobs'),
  getJob: (id) => api.get(`/migration/jobs/${id}`),
  simulate: (data) => api.post('/migration/simulate', data),
  upload: (formData) => api.post('/migration/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const rag = {
  ingest: (id) => api.post(`/rag/ingest/${id}`),
  ingestAll: () => api.post('/rag/ingest-all'),
  ask: (question, top_k) => api.post('/rag/ask', { question, top_k }),
  askPublic: (question, top_k) => api.post('/rag/ask-public', { question, top_k }),
  status: () => api.get('/rag/status'),
};

export const nlSearch = {
  generate: (question) => api.post('/nl-search/generate', { question }),
  execute: (sql, database = 'records') => api.post('/nl-search/execute', { sql, database }),
};

export const redaction = {
  analyze: (recordId) => api.post(`/redaction/analyze/${recordId}`),
  apply: (jobId, selected_entities) => api.post(`/redaction/apply/${jobId}`, { selected_entities }),
  jobs: (recordId) => api.get(`/redaction/jobs/${recordId}`),
};

export const admin = {
  stats: () => api.get('/admin/stats'),
  users: () => api.get('/admin/users'),
  audit: () => api.get('/admin/audit'),
  demoRequests: () => api.get('/demo-requests'),
  updateDemoRequest: (id, data) => api.patch(`/demo-requests/${id}`, data),
};

export default api;
